import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Chart as ChartJS,
  type ChartType,
  type TooltipItem,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Chart as MixedChart } from "react-chartjs-2";
import { getInsights } from "@/lib/api";
import { fmtMoney } from "@/lib/money";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Filler,
  Tooltip,
  Legend,
);

/**
 * Series colors come from the design tokens in index.css (--chart-*), which
 * are validated (dataviz six-checks) against both portal card surfaces:
 * dark #151d2e, light #ffffff. Identity is never color-alone — every chart
 * has a legend or labeled rows, plus tooltips.
 *
 * Chart.js takes paint values, not CSS vars, so the tokens are resolved to
 * concrete strings here and re-resolved whenever the theme flips.
 */
const CHART_TOKENS = {
  in: "--chart-in",
  out: "--chart-out",
  balance: "--chart-balance",
  net: "--chart-net",
  ink: "--color-text-muted",
  grid: "--chart-grid",
  tooltipBg: "--color-bg-elevated",
  tooltipInk: "--color-text-heading",
} as const;

type ChartColors = Record<keyof typeof CHART_TOKENS, string>;

function readChartColors(): ChartColors {
  const style = getComputedStyle(document.body);
  return Object.fromEntries(
    Object.entries(CHART_TOKENS).map(([k, v]) => [k, style.getPropertyValue(v).trim()]),
  ) as ChartColors;
}

/** Re-render charts when the theme toggles (body class flips). */
function useChartColors(): ChartColors {
  const [colors, setColors] = useState(readChartColors);
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(readChartColors()));
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleString("en-GB", { month: "short", timeZone: "UTC" });

export function InsightsTab() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const { data, isLoading } = useQuery({
    queryKey: ["fin-insights", year],
    queryFn: () => getInsights(year),
  });
  const c = useChartColors();

  const baseOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: c.tooltipBg,
          titleColor: c.tooltipInk,
          bodyColor: c.tooltipInk,
          borderColor: c.grid,
          borderWidth: 1,
          padding: 10,
          cornerRadius: 6,
          callbacks: {
            label: (ctx: TooltipItem<ChartType>) => {
              const parsed = ctx.parsed as { y?: number | null; x?: number | null };
              const v = parsed.y ?? parsed.x ?? 0;
              return ` ${ctx.dataset.label ?? ""}: ${fmtMoney(v)}`.trimStart();
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: c.ink, font: { size: 11 } } },
        y: {
          grid: { color: c.grid },
          border: { display: false },
          ticks: {
            color: c.ink,
            font: { size: 11 },
            callback: (v: number | string) => fmtMoney(Number(v)),
          },
        },
      },
    }),
    [c],
  );

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-20 text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-lg" />
      </div>
    );
  }

  const hasData = data.balanceSeries.length > 0;

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex items-center gap-2">
        {[thisYear - 1, thisYear].map((y) => (
          <button
            key={y}
            onClick={() => setYear(y)}
            className={`rounded-token-md border px-3 py-1.5 text-sm transition-colors ${
              year === y
                ? "border-accent bg-accent-muted font-medium text-accent"
                : "border-border text-text-secondary hover:text-text-heading"
            }`}
          >
            {y}
          </button>
        ))}
        <span className="ml-auto text-xs text-text-muted">
          Underlying figures live in{" "}
          <span className="font-medium text-text-secondary">Statements</span> (the table view)
        </span>
      </div>

      {!hasData ? (
        <div className="rounded-token-lg border border-dashed border-border bg-bg-card/40 px-6 py-16 text-center">
          <p className="text-sm text-text-secondary">
            No transactions yet — import a statement or add entries in the Ledger and the
            charts light up.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {/* Cash balance over time — single series, no legend (title names it) */}
          <ChartCard
            title="Cash balance"
            subtitle="running balance, all time"
          >
            <Line
              data={{
                labels: data.balanceSeries.map((p) =>
                  new Date(p.date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  }),
                ),
                datasets: [
                  {
                    label: "Balance",
                    data: data.balanceSeries.map((p) => p.balance),
                    borderColor: c.balance,
                    // 10% fill under the line. Relies on --chart-balance being
                    // a 6-digit hex in both themes (see index.css).
                    backgroundColor: `${c.balance}1a`,
                    fill: true,
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    pointHitRadius: 12,
                    tension: 0.25,
                  },
                ],
              }}
              options={baseOptions}
            />
          </ChartCard>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Monthly in vs out + net — 2 series bars + derived net line */}
            <ChartCard title="Money in vs out" subtitle={`${year}, by month`}>
              <MixedChart
                type="bar"
                data={{
                  labels: data.monthly.map((m) => MONTH_LABEL(m.month)),
                  datasets: [
                    {
                      label: "In",
                      data: data.monthly.map((m) => m.in),
                      backgroundColor: c.in,
                      borderRadius: 4,
                      borderSkipped: "start" as const,
                      barPercentage: 0.72,
                      categoryPercentage: 0.72,
                    },
                    {
                      label: "Out",
                      data: data.monthly.map((m) => m.out),
                      backgroundColor: c.out,
                      borderRadius: 4,
                      borderSkipped: "start" as const,
                      barPercentage: 0.72,
                      categoryPercentage: 0.72,
                    },
                    {
                      label: "Net",
                      data: data.monthly.map((m) => m.net),
                      type: "line" as const,
                      borderColor: c.net,
                      borderDash: [4, 3],
                      borderWidth: 2,
                      pointRadius: 0,
                      pointHoverRadius: 4,
                      pointHitRadius: 12,
                      tension: 0.2,
                    },
                  ],
                }}
                options={{
                  ...baseOptions,
                  plugins: {
                    ...baseOptions.plugins,
                    legend: {
                      display: true,
                      position: "top" as const,
                      align: "end" as const,
                      labels: {
                        color: c.ink,
                        usePointStyle: true,
                        boxWidth: 8,
                        boxHeight: 8,
                        font: { size: 11 },
                      },
                    },
                  },
                }}
              />
            </ChartCard>

            {/* Expense categories — magnitude, single hue, labeled rows */}
            <ChartCard title="Spending by category" subtitle={`${year}, expenses`}>
              {data.categories.length === 0 ? (
                <p className="grid h-full place-items-center text-sm text-text-muted">
                  No expenses recorded this year.
                </p>
              ) : (
                <Bar
                  data={{
                    labels: data.categories.map((x) => x.name),
                    datasets: [
                      {
                        label: "Spent",
                        data: data.categories.map((x) => x.total),
                        backgroundColor: c.balance,
                        borderRadius: 4,
                        borderSkipped: "start" as const,
                        barPercentage: 0.7,
                        categoryPercentage: 0.8,
                      },
                    ],
                  }}
                  options={{
                    ...baseOptions,
                    indexAxis: "y" as const,
                    interaction: { mode: "nearest" as const, intersect: false },
                    scales: {
                      x: {
                        grid: { color: c.grid },
                        border: { display: false },
                        ticks: {
                          color: c.ink,
                          font: { size: 11 },
                          callback: (v: number | string) => fmtMoney(Number(v)),
                        },
                      },
                      y: { grid: { display: false }, ticks: { color: c.ink, font: { size: 11 } } },
                    },
                  }}
                />
              )}
            </ChartCard>
          </div>
        </div>
      )}
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-token-lg border border-border bg-bg-card p-4">
      <div className="mb-3">
        <span className="text-sm font-semibold text-text-heading">{title}</span>
        <span className="ml-2 text-xs text-text-muted">{subtitle}</span>
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
}
